/**
 * The room types the app offers for a room, in its order: the `RoomTypes` list of its room builder
 * (`Assets/Resources/Gui/Icons2/RoomBuilder/roombuilder.xml`, read through `RoomBuilderStorage`), each by the
 * name a project stores in `RoomType` and the app's translation key of its title. The list ends, as the app's
 * does, with «Комната», the empty type of a room with no type chosen (`RoomTypes.AnyRoom`); «План помещения»
 * (`all`) is a project's type, not a room's, and is left out.
 */
import { t, type TranslationKey } from '../i18n/I18n';

const type = (id: string, key: TranslationKey) => ({ id, key, get title() { return t(key); } });

export const ROOM_TYPES: ReadonlyArray<{ id: string; key: TranslationKey; readonly title: string }> = [
  type('bedroom', '#RoomBedroom'),
  type('livingroom', '#RoomLiving'),
  type('kitchens', '#RoomKitchen'),
  type('outdoor', '#RoomOutdoor'),
  type('kids', '#RoomBabyKids'),
  type('bathroom', '#RoomBath'),
  type('closet', '#RoomCloset'),
  type('store', '#RoomStore'),
  type('homeoffice', '#RoomHomeOffice'),
  type('cinema', '#RoomHomeCinema'),
  type('cafe', '#RoomCafe'),
  type('garage', '#RoomGarage'),
  type('hall', '#RoomHall'),
  type('dining', '#RoomDining'),
  type('gym', '#RoomGym'),
  type('laundry', '#RoomLaundry'),
  type('reception', '#RoomReception'),
  type('coworking', '#RoomCoworking'),
  type('salon', '#RoomSalon'),
  type('event', '#RoomEvent'),
  type('', '#RoomAll'),
];

/** Names older projects and the web's own rooms carry beside the app's list. */
const ALIASES: Record<string, TranslationKey> = {
  kitchen: '#RoomKitchen', living: '#RoomLiving', diningroom: '#RoomDining', office: '#RoomHomeOffice', nursery: '#RoomBabyKids',
  toilet: '#TagToilet', balcony: '#TagBalcony', wardrobe: '#RoomCloset',
};

/** The title of a stored room type; undefined for one the app does not know. */
export function roomTypeTitle(type: string): string | undefined {
  const key = type.trim().toLowerCase();
  const alias = ALIASES[key];
  return ROOM_TYPES.find(value => value.id === key)?.title ?? (alias ? t(alias) : undefined);
}

/** Whether a room's type says what the room is: the app asks for one while it is «Комната» or unknown. */
export const hasRoomType = (type: string) => !!type.trim() && roomTypeTitle(type) !== undefined;
